import "@/App.css";
import axios from "axios";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, useSearchParams, Navigate } from "react-router-dom";

// Runtime safety net: if the build-time REACT_APP_BACKEND_URL points at a
// different host than the one currently serving us (e.g., custom-domain
// deploys where the bundle was cached against the old preview host), silently
// rewrite outbound API calls to use the current origin instead. Kubernetes
// ingress routes /api/* to the backend regardless of host, so this Just
// Works on every domain the app gets deployed to. No-op when the env var
// already matches the host (preview + local dev).
(() => {
    if (typeof window === "undefined") return;
    const envBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    if (!envBase) return;
    let envHost;
    try { envHost = new URL(envBase).host; } catch { return; }
    if (envHost === window.location.host) return;  // already correct
    // We only rewrite when we're on a real custom-domain origin (not localhost,
    // not the preview host) to avoid masking actual misconfigurations in dev.
    const here = window.location.hostname;
    if (here === "localhost" || here.endsWith(".emergent.host") || here.endsWith(".preview.emergentagent.com")) {
        return;
    }
    axios.interceptors.request.use((config) => {
        if (config.url && config.url.startsWith(envBase)) {
            config.url = window.location.origin + config.url.slice(envBase.length);
        }
        return config;
    });
    // eslint-disable-next-line no-console
    console.info("[api] rewriting requests from", envBase, "→", window.location.origin);
})();

// ────────────────────────────────────────────────────────────────────
// Resilience: auto-retry on transient backend hiccups
// ────────────────────────────────────────────────────────────────────
//
// The deployed backend on maxsc.net occasionally returns Cloudflare 5xx
// errors when the FastAPI worker restarts mid-request (OOM kill, redeploy,
// autoscale event). The new worker comes up in <2s but the in-flight
// request that was killed never gets a response, so Cloudflare serves a
// 520/521 origin-error page. Users see "Couldn't load roster" or
// "Couldn't save" for a single failed request that would succeed if the
// browser just tried again.
//
// This interceptor retries idempotent-looking requests up to 2 times with
// short backoff. Limits:
//   - Only GET, HEAD, OPTIONS, and explicitly-opted-in mutations
//     (we mark sensitive POSTs with config.headers['X-Retry-Safe'] = '1'
//     so we never double-bill a parent — most POSTs are NOT retried)
//   - Only on signatures that are transient (520-524, network error, no
//     response). Genuine 4xx and 5xx errors that include a body are
//     passed through unchanged.
//   - Backoff is small (400ms, 1200ms) so the user doesn't notice — at
//     worst the page takes ~1.5s longer than usual on a bad day.
// 2026-02-17.
(() => {
    if (typeof window === "undefined") return;
    const RETRY_STATUSES = new Set([520, 521, 522, 523, 524]);
    const RETRY_METHODS = new Set(["get", "head", "options"]);
    const BACKOFFS_MS = [400, 1200];
    axios.interceptors.response.use(undefined, async (error) => {
        const cfg = error?.config;
        if (!cfg) return Promise.reject(error);
        const method = (cfg.method || "get").toLowerCase();
        const isRetrySafePost = cfg.headers?.["X-Retry-Safe"] === "1";
        if (!RETRY_METHODS.has(method) && !isRetrySafePost) return Promise.reject(error);
        // Skip if we've already exhausted attempts on this config.
        cfg.__retryCount = (cfg.__retryCount || 0);
        if (cfg.__retryCount >= BACKOFFS_MS.length) return Promise.reject(error);
        // Decide whether this error is the transient kind we retry on.
        const status = error?.response?.status;
        const hasBody = !!error?.response?.data;
        const isCloudflare5xx = status && RETRY_STATUSES.has(status);
        const isNetworkError = !error?.response;  // no response from origin
        const isEmptyOriginResponse = status === 502 && !hasBody;
        const retryable = isCloudflare5xx || isNetworkError || isEmptyOriginResponse;
        if (!retryable) return Promise.reject(error);
        const wait = BACKOFFS_MS[cfg.__retryCount];
        cfg.__retryCount += 1;
        // eslint-disable-next-line no-console
        console.info(`[api] transient ${status || "network"} on ${method.toUpperCase()} ${cfg.url} — retrying in ${wait}ms (attempt ${cfg.__retryCount}/${BACKOFFS_MS.length})`);
        await new Promise((r) => setTimeout(r, wait));
        return axios(cfg);
    });
})();


import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import SchoolProgram from "@/pages/SchoolProgram";
import SchoolTierPage from "@/pages/SchoolTierPage";
import ProProgram from "@/pages/ProProgram";
import AdultPerformance from "@/pages/AestheticProgram";
import Coaches from "@/pages/Coaches";
import Careers from "@/pages/Careers";
import AddCardLink from "@/pages/AddCardLink";
import DemoModeBanner from "@/components/DemoModeBanner";
import Dashboard from "@/pages/Dashboard";
import WelcomeAddCard from "@/pages/WelcomeAddCard";
import WelcomeWaiver from "@/pages/WelcomeWaiver";
import RefundPolicy from "@/pages/legal/RefundPolicy";
import CoachDashboard from "@/pages/CoachDashboard";
import CoachManagement from "@/pages/CoachManagement";
import CoachInviteAccept from "@/pages/CoachInviteAccept";
import ExerciseLibrary from "@/pages/coach/ExerciseLibrary";
import Equipment from "@/pages/coach/Equipment";
import WorkoutsList from "@/pages/coach/WorkoutsList";
import WorkoutBuilder from "@/pages/coach/WorkoutBuilder";
import AthleteWorkouts from "@/pages/athlete/AthleteWorkouts";
import TrainingCategories from "@/pages/athlete/TrainingCategories";
import AthleteProgramsList from "@/pages/athlete/ProgramsList";
import AthleteProgramDetail from "@/pages/athlete/ProgramDetail";
import AthleteWorkoutView from "@/pages/athlete/AthleteWorkoutView";
import WorkoutRunner from "@/pages/athlete/WorkoutRunner";
import AthleteHome from "@/pages/athlete/AthleteHome";
import AthleteSchedule from "@/pages/athlete/AthleteSchedule";
import MemberSchedule from "@/pages/athlete/Schedule";
import CoachSchedule from "@/pages/coach/Schedule";
import CoachCheckIn from "@/pages/coach/CheckIn";
import CoachAnnouncements from "@/pages/coach/Announcements";
import PendingAssessments from "@/pages/coach/PendingAssessments";
import WorkoutAccessAudit from "@/pages/coach/WorkoutAccessAudit";
import AthleteDossier from "@/pages/coach/AthleteDossier";
import CoachWorkoutRunner from "@/pages/coach/CoachWorkoutRunner";
// Legacy Plans/Packs coach editors retired 2026-06 — Pricing Blocks is the
// single pricing source of truth; old URLs redirect there.
import PricingBlocks from "@/pages/coach/PricingBlocks";
import TaxLiability from "@/pages/coach/TaxLiability";
import CoachRevenue from "@/pages/coach/Revenue";
import CurrentSubscriptions from "@/pages/coach/CurrentSubscriptions";
import AutoRenews from "@/pages/coach/AutoRenews";
import Waivers from "@/pages/coach/Waivers";
import CoachAccounting from "@/pages/coach/Accounting";
import CoachAttendance from "@/pages/coach/Attendance";
import CoachWeeklyMetrics from "@/pages/coach/WeeklyMetrics";
import ParentLayout from "@/components/parent/ParentLayout";
import CoachMnp from "@/pages/coach/Mnp";
import PeopleSearch from "@/pages/coach/PeopleSearch";
import CoachChangeRequests from "@/pages/coach/ChangeRequests";
import Signup from "@/pages/Signup";
import ChatAnalytics from "@/pages/coach/ChatAnalytics";
import CoachApplications from "@/pages/coach/Applications";
import AssessmentSession from "@/pages/coach/AssessmentSession";
import AssessmentsHub from "@/pages/coach/AssessmentsHub";
import CoachContract from "@/pages/coach/CoachContract";
import ContractEditor from "@/pages/coach/ContractEditor";
import ActivityLog from "@/pages/coach/ActivityLog";
import ProgramsList from "@/pages/coach/ProgramsList";
import CoachMessages from "@/pages/coach/Messages";
import MemberMessages from "@/pages/dashboard/Messages";
import TodayAttendance from "@/pages/coach/TodayAttendance";
import RenewalForecast from "@/pages/coach/RenewalForecast";
import Library from "@/pages/coach/Library";
import Clubs from "@/pages/coach/Clubs";
import MyPricing from "@/pages/MyPricing";
import ProgressUpdates from "@/pages/coach/ProgressUpdates";
import AthletePrograms from "@/pages/AthletePrograms";
import EnrollPage, { EnrollSuccess } from "@/pages/EnrollPage";
import ContinuePage from "@/pages/Continue";
import AutoRenewManage from "@/pages/AutoRenewManage";
import AssessmentHistory from "@/pages/AssessmentHistory";
import LiveAssessment from "@/pages/LiveAssessment";
import MemberBilling from "@/pages/athlete/Billing";
import Waiver from "@/pages/Waiver";
import WaiverRead from "@/pages/WaiverRead";
import MembershipPolicy from "@/pages/MembershipPolicy";
import AuthCallback from "@/pages/AuthCallback";
import AuthDiagnose from "@/pages/AuthDiagnose";
import AuthRestore from "@/pages/AuthRestore";
import MagicLinkVerify from "@/pages/MagicLinkVerify";
import CoachLayout from "@/components/coach/CoachLayout";
import CoachAuthHealth from "@/pages/coach/AuthHealth";
import SystemHealth from "@/pages/coach/SystemHealth";
import CustomerJourney from "@/pages/coach/CustomerJourney";
import UnsettledIous from "@/pages/coach/UnsettledIous";
import NewPackageSoon from "@/pages/coach/NewPackageSoon";
import JourneyLaunch from "@/pages/JourneyLaunch";
import SandboxModeBanner from "@/components/SandboxModeBanner";
import { AuthProvider } from "@/context/AuthContext";

function AppRouter() {
    const location = useLocation();
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <>
            <ScrollToTop pathname={location.pathname} />
            <Routes>
            <Route element={<Layout />}>
                <Route path="/" element={<Home />} />
                <Route path="/programs/school" element={<SchoolProgram />} />
                <Route path="/programs/school/:tierSlug" element={<SchoolTierPage />} />
                <Route path="/programs/youth" element={<Navigate to="/programs/school" replace />} />
                <Route path="/programs/pro" element={<ProProgram />} />
                <Route path="/programs/adult" element={<AdultPerformance />} />
                <Route path="/programs/aesthetic" element={<Navigate to="/programs/adult" replace />} />
                <Route path="/coaches" element={<Coaches />} />
                <Route path="/careers" element={<Careers />} />
                <Route path="/waiver" element={<Waiver />} />
                <Route path="/waiver-read" element={<WaiverRead />} />
                <Route path="/policy/membership" element={<MembershipPolicy />} />
                <Route path="/add-card/:token" element={<AddCardLink />} />
                <Route path="/legal/refund-policy" element={<RefundPolicy />} />
            </Route>
            {/* Live parent view — bypasses public Layout chrome so the phone view is clean */}
            <Route path="/live/:liveViewId" element={<LiveAssessment />} />
            <Route element={<Layout />}>
                <Route element={<ParentLayout />}>
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/welcome/add-card" element={<WelcomeAddCard />} />
                    <Route path="/welcome/waiver" element={<WelcomeWaiver />} />
                    <Route path="/dashboard/workouts" element={<WorkoutsRouter />} />
                    <Route path="/dashboard/programs" element={<AthleteProgramsList />} />
                    <Route path="/dashboard/programs/:programId" element={<AthleteProgramDetail />} />
                    <Route path="/dashboard/train" element={<TrainingCategories />} />
                    <Route path="/dashboard/workouts/:workoutId" element={<AthleteWorkoutView />} />
                    <Route path="/dashboard/workouts/combined/run" element={<WorkoutRunner />} />
                    <Route path="/dashboard/workouts/:workoutId/run" element={<WorkoutRunner />} />
                    <Route path="/dashboard/schedule" element={<MemberSchedule />} />
                    <Route path="/dashboard/billing" element={<MemberBilling />} />
                    <Route path="/dashboard/billing/auto-renew" element={<AutoRenewManage />} />
                    <Route path="/dashboard/assessments" element={<AssessmentHistory />} />
                    <Route path="/dashboard/messages" element={<MemberMessages />} />
                </Route>
                <Route path="/athlete" element={<AthleteHome />} />
                <Route path="/athlete/schedule" element={<AthleteSchedule />} />
                <Route element={<CoachLayout />}>
                    <Route path="/coach" element={<CoachDashboard />} />
                    <Route path="/coach/leads/:userId" element={<CoachDashboard />} />
                    <Route path="/coach/coaches" element={<CoachManagement />} />
                    <Route path="/coach/schedule" element={<CoachSchedule />} />
                    <Route path="/coach/check-in" element={<CoachCheckIn />} />
                    <Route path="/coach/announcements" element={<CoachAnnouncements />} />
                    <Route path="/coach/assessment-requests" element={<PendingAssessments />} />
                    <Route path="/coach/access-audit" element={<WorkoutAccessAudit />} />
                    <Route path="/coach/assessments" element={<AssessmentsHub />} />
                    <Route path="/coach/contract" element={<CoachContract />} />
                    <Route path="/coach/coaches/contract-editor" element={<ContractEditor />} />
                    <Route path="/coach/assessment/:sessionId" element={<AssessmentSession />} />
                    <Route path="/coach/athletes/:athleteId" element={<AthleteDossier />} />
                    <Route path="/coach/athletes/:athleteId/run/:workoutId" element={<CoachWorkoutRunner />} />
                    <Route path="/coach/billing/plans" element={<Navigate to="/coach/pricing-blocks" replace />} />
                    <Route path="/coach/billing/packs" element={<Navigate to="/coach/pricing-blocks" replace />} />
                    <Route path="/coach/pricing-blocks" element={<PricingBlocks />} />
                    <Route path="/coach/tax-liability" element={<TaxLiability />} />
                    <Route path="/coach/revenue" element={<CoachRevenue />} />
                    <Route path="/coach/current-subscriptions" element={<CurrentSubscriptions />} />
                    <Route path="/coach/auto-renews" element={<AutoRenews />} />
                    <Route path="/coach/waivers" element={<Waivers />} />
                    <Route path="/coach/accounting" element={<CoachAccounting />} />
                    <Route path="/coach/attendance" element={<CoachAttendance />} />
                    <Route path="/coach/renewal-forecast" element={<RenewalForecast />} />
                    <Route path="/coach/metrics" element={<CoachWeeklyMetrics />} />
                    <Route path="/coach/mnp" element={<CoachMnp />} />
                    <Route path="/coach/find" element={<PeopleSearch />} />
                    <Route path="/coach/change-requests" element={<CoachChangeRequests />} />
                    <Route path="/coach/chat-analytics" element={<ChatAnalytics />} />
                    <Route path="/coach/applications" element={<CoachApplications />} />
                    <Route path="/coach/activity" element={<ActivityLog />} />
                    <Route path="/coach/programs" element={<ProgramsList />} />
                    <Route path="/coach/messages" element={<CoachMessages />} />
                    <Route path="/coach/today" element={<TodayAttendance />} />
                    <Route path="/coach/library" element={<Library />} />
                    <Route path="/coach/clubs" element={<Clubs />} />
                    <Route path="/dashboard/pricing" element={<MyPricing />} />
                    <Route path="/coach/progress-updates" element={<ProgressUpdates />} />
                    <Route path="/coach/exercises" element={<ExerciseLibrary />} />
                    <Route path="/coach/equipment" element={<Equipment />} />
                    <Route path="/coach/workouts" element={<WorkoutsList />} />
                    <Route path="/coach/workouts/new" element={<WorkoutBuilder />} />
                    <Route path="/coach/workouts/:workoutId/edit" element={<WorkoutBuilder />} />
                    <Route path="/coach/auth-health" element={<CoachAuthHealth />} />
                    <Route path="/coach/system-health" element={<SystemHealth />} />
                    <Route path="/coach/customer-journey" element={<CustomerJourney />} />
                    <Route path="/coach/unsettled-ious" element={<UnsettledIous />} />
                    <Route path="/coach/new-package-soon" element={<NewPackageSoon />} />
                </Route>
                <Route path="/programs" element={<AthletePrograms />} />
                <Route path="/enroll/:token" element={<EnrollPage />} />
                <Route path="/enroll/:token/success" element={<EnrollSuccess />} />
                <Route path="/continue/:assessmentId" element={<ContinuePage />} />
                <Route path="/signup" element={<Signup />} />
            </Route>
            <Route path="/auth/magic" element={<MagicLinkVerify />} />
            <Route path="/auth/diagnose" element={<AuthDiagnose />} />
            <Route path="/auth/restore" element={<AuthRestore />} />
            <Route path="/journey/launch" element={<JourneyLaunch />} />
            <Route path="/coach/accept" element={<CoachInviteAccept />} />
        </Routes>
        </>
    );
}

/**
 * ScrollToTop — fires window.scrollTo(0,0) whenever the route pathname
 * changes so coaches don't land halfway down a long lead-detail page when
 * they switch between Roster ↔ Schedule ↔ Accounting etc. Uses an effect
 * keyed on `pathname` so query-string changes (search, filters) don't
 * disrupt the user's current scroll position. Smooth scroll feels nicer
 * than a teleport on desktop; `instant` is fine for mobile (motion-reduce).
 */
function ScrollToTop({ pathname }) {
    useEffect(() => {
        // Use the *next* paint so it lands after the new page mounts and
        // any synchronous focus-shifts run, otherwise some pages restore
        // their own scroll position on mount and we lose the reset.
        const id = window.requestAnimationFrame(() => {
            window.scrollTo({ top: 0, left: 0, behavior: "instant" });
        });
        return () => window.cancelAnimationFrame(id);
    }, [pathname]);
    return null;
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <SandboxModeBanner />
                    <DemoModeBanner />
                    <AppRouter />
                </AuthProvider>
            </BrowserRouter>
            <Toaster theme="dark" />
        </div>
    );
}

// Tiny router shim: /dashboard/workouts with no category → the 5-S landing
// page; with ?category=strength etc. → the filtered workout list. Keeps the
// existing URL stable so older bookmarks + email deep links don't break,
// while giving athletes a category chooser by default. (Sachin 2026-02-16.)
function WorkoutsRouter() {
    const [searchParams] = useSearchParams();
    return searchParams.get("category")
        ? <AthleteWorkouts />
        : <TrainingCategories />;
}


export default App;
